import { useState, useEffect } from 'react';
import { Close } from '@beamery/lib-ds-icons';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner } from '@beamery/lib-ds-components';
import { CandidateForm } from '../candidate-form';
import { EmbeddedNotFound } from '../embedded-not-found';
import { logClientMessage } from '../../api/logger';
import { embeddedClassNamesPrefix, hasIdsOrSlugs } from '../../utils/embedded';
import {
  Header,
  CloseButton,
  ContainerWrapper,
  FormWrapper,
  ScrollableContainer,
} from './candidate-form-embedded.styles';
import type {
  CandidateFormEmbeddedProps,
  CandidateFormEmbeddedWrapperProps,
} from './candidate-form-embedded.interface';

// Used as hooks so embedders can overwrite styling
// Warning: changing these may break their styling
const formClassName = `${embeddedClassNamesPrefix}-form`;
const backgroundClassName = `${formClassName}-inner-container`;

const CandidateFormEmbeddedWrapper = ({
  onClose,
  enableScrollbar = false,
  children,
}: CandidateFormEmbeddedWrapperProps) => {
  const { t } = useTranslation();
  return (
    <ContainerWrapper className={formClassName}>
      <FormWrapper
        className={backgroundClassName}
        $enableScrollbar={enableScrollbar}
      >
        {onClose && (
          <Header>
            <CloseButton variant='secondary' onPress={onClose}>
              <Close
                size='large'
                title={t('candidate-experience.embedded.modal.close')}
              />
            </CloseButton>
          </Header>
        )}
        <ScrollableContainer $enableScrollbar={enableScrollbar}>
          {children}
        </ScrollableContainer>
      </FormWrapper>
    </ContainerWrapper>
  );
};

const LoadingWrapper = ({
  onClose,
}: Pick<CandidateFormEmbeddedWrapperProps, 'onClose'>) => (
  <CandidateFormEmbeddedWrapper onClose={onClose}>
    <LoadingSpinner />
  </CandidateFormEmbeddedWrapper>
);

const NotFoundWrapper = ({
  onClose,
}: Pick<CandidateFormEmbeddedWrapperProps, 'onClose'>) => (
  <CandidateFormEmbeddedWrapper onClose={onClose}>
    <EmbeddedNotFound />
  </CandidateFormEmbeddedWrapper>
);

const renderFormContents =
  (
    enableScrollbar: boolean,
    onClose?: CandidateFormEmbeddedWrapperProps['onClose'],
  ) =>
  (form: JSX.Element) => (
    <CandidateFormEmbeddedWrapper
      onClose={onClose}
      enableScrollbar={enableScrollbar}
    >
      {form}
    </CandidateFormEmbeddedWrapper>
  );

export const CandidateFormEmbedded = ({
  companyId,
  formId,
  formSlug,
  companySlug,
  onClose,
  enableScrollbar = false,
}: CandidateFormEmbeddedProps) => {
  const [page, setPage] = useState(0);

  const hasValidProps = hasIdsOrSlugs({
    formId,
    formSlug,
    companyId,
    companySlug,
  });

  useEffect(() => {
    if (!hasValidProps) {
      // eslint-disable-next-line no-console
      console.error(
        `Beamery Convert Flow: Could not render. The ids or slugs attributes are missing from the element.`,
      );
      void logClientMessage({
        value: 'Embedded flow error. Missing flowId or flowSlug.',
      });
    }
  }, [hasValidProps]);

  if (!hasValidProps || page === undefined) {
    return <NotFoundWrapper onClose={onClose} />;
  }

  return (
    <CandidateForm
      formSlug={formSlug}
      companySlug={companySlug}
      formId={formId}
      companyId={companyId}
      notFoundComponent={<NotFoundWrapper onClose={onClose} />}
      loadingComponent={<LoadingWrapper onClose={onClose} />}
      contentComponent={renderFormContents(enableScrollbar, onClose)}
      page={page}
      onPageChange={setPage}
      urlParameterString={window.location.search}
    />
  );
};
